import { isString } from "lodash";
import type { SiteTheme } from "@circle-react-shared/BuilderUI/SiteThemes/constants";
import { resolveCssVariable } from "@circle-react-shared/BuilderUI/ThemeConfiguration/SiteStyles/Tokens/utils/resolveCssVariable";
import {
  type StyleDefinition,
  styleMap,
} from "@circle-react-shared/BuilderUI/utils/styleMap";

/**
 * Flattens nested theme variables into a single level object
 * Email themes nest variables by category (light, h1-h6, p1-p3, buttons)
 * but CSS resolution needs them all at the same level
 */
const flattenThemeVariables = (theme: SiteTheme): Record<string, string> => {
  const result: Record<string, string> = {};
  for (const value of Object.values(theme.cssVariables)) {
    if (value && typeof value === "object") {
      Object.assign(result, value);
    }
  }
  return result;
};

export const resolveVariableValuesFromStyleObject = ({
  styleObject,
  currentTheme,
}: {
  styleObject: object | null;
  currentTheme: SiteTheme;
}) => {
  if (!styleObject) return {};

  const allVariables = flattenThemeVariables(currentTheme);

  return Object.entries(styleObject).reduce(
    (acc, [key, value]) => {
      // Only resolve CSS variables for string values
      if (typeof value === "string") {
        acc[key] = resolveCssVariable({
          value: value,
          cssVariables: allVariables,
        });
      } else {
        // For non-string values, use them as-is
        acc[key] = value as string | number;
      }
      return acc;
    },
    {} as Record<string, string | number>,
  );
};

const suffixWithPx = (value: number | string): string => {
  if (isString(value) && value.endsWith("%")) {
    return value;
  }
  return `${value}px`;
};

export const getVisualEmailBuilderStyleMap = (
  theme: SiteTheme,
): Record<string, StyleDefinition> => {
  const allVariables = flattenThemeVariables(theme);

  return {
    ...styleMap,
    fontSize: {
      cssProperty: "fontSize",
      processorType: "unit",
      defaultUnit: "px",
    },
    borderTopWidth: {
      cssProperty: "borderTopWidth",
      processorType: "unit",
      defaultUnit: "px",
    },
    fontWeight: {
      cssProperty: "fontWeight",
      processorType: "custom",
      processor: value => {
        const val = parseInt(String(value.value), 10);
        return isNaN(val)
          ? resolveCssVariable({
              value: String(value.value),
              cssVariables: allVariables,
            })
          : val;
      },
    },
    borderRadius: {
      cssProperty: "borderRadius",
      processorType: "custom",
      processor: value => {
        if (typeof value.value === "object") {
          const corners = resolveVariableValuesFromStyleObject({
            styleObject: value.value,
            currentTheme: theme,
          }) as {
            border_left_top_radius: number | string;
            border_right_top_radius: number | string;
            border_right_bottom_radius: number | string;
            border_left_bottom_radius: number | string;
          };

          return `${suffixWithPx(corners.border_left_top_radius)} ${suffixWithPx(corners.border_right_top_radius)} ${suffixWithPx(corners.border_right_bottom_radius)} ${suffixWithPx(corners.border_left_bottom_radius)}`;
        }
        return typeof value.value === "number" ? `${value.value}px` : undefined;
      },
    },
    padding: {
      cssProperty: "padding",
      processorType: "custom",
      processor: value => {
        const val = value.value as {
          paddingTop?: number | string;
          paddingBottom?: number | string;
          paddingLeft?: number | string;
          paddingRight?: number | string;
        };

        if (typeof value.value === "object") {
          const { paddingTop, paddingBottom, paddingLeft, paddingRight } =
            resolveVariableValuesFromStyleObject({
              styleObject: val,
              currentTheme: theme,
            });

          return `${paddingTop}px ${paddingRight}px ${paddingBottom}px ${paddingLeft}px`;
        }
        return typeof value.value === "number" ? `${value.value}px` : undefined;
      },
    },
    margin: {
      cssProperty: "margin",
      processorType: "custom",
      processor: value => {
        const val = value.value as {
          marginTop?: number | string;
          marginBottom?: number | string;
          marginLeft?: number | string;
          marginRight?: number | string;
        };

        if (typeof value.value === "object") {
          const { marginTop, marginBottom, marginLeft, marginRight } =
            resolveVariableValuesFromStyleObject({
              styleObject: val,
              currentTheme: theme,
            });
          return `${marginTop}px ${marginRight}px ${marginBottom}px ${marginLeft}px`;
        }

        return undefined;
      },
    },
    opacity: {
      cssProperty: "opacity",
      processorType: "unit",
      defaultUnit: "",
    },
  };
};
